import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const PRESENCE_URL = "https://www.viva-artist.com/oeuvre/b10df749-0e49-4ed7-b4a4-861c933b5855";
const STAGE_IMAGES = { 0: "/images/lieux/interieur-87.webp", 3: "/images/lieux/baie-abbaye-01.jpeg" };

const StageImage = ({ src, alt }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return (
    <div ref={ref} className="gold-frame relative aspect-[4/5] overflow-hidden">
      <motion.img style={{ y }} src={src} alt={alt} className="h-[115%] w-full object-cover object-top" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
    </div>
  );
};

const Presence = () => {
  const { t } = useLang();

  const goBoutique = () => {
    if (window.__lenis) window.__lenis.scrollTo("#boutique", { duration: 1.4 });
    else document.querySelector("#boutique")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="presence" data-testid="presence-section" className="relative overflow-hidden">
      <span aria-hidden="true" className="pointer-events-none absolute -left-10 top-16 select-none font-display text-[20vw] leading-none text-transparent opacity-[0.06]" style={{ WebkitTextStroke: "1px rgb(var(--gold))" }}>
        PRÉSENCE
      </span>

      <div className="relative mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.presence.kicker}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-serif text-3xl font-light text-parchment sm:text-4xl lg:text-5xl">
            {t.presence.title}
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-sand sm:text-lg">
            {t.presence.intro}
          </p>
        </Reveal>

        <div className="relative mt-20 lg:mt-28">
          <div aria-hidden="true" className="absolute bottom-0 left-[19px] top-0 w-px bg-gradient-to-b from-gold/50 via-gold/20 to-gold/50 lg:left-1/2" />

          <div className="space-y-20 lg:space-y-32">
            {t.presence.stages.map((stage, i) => {
              const image = STAGE_IMAGES[i];
              const flip = i % 2 === 1;
              return (
                <article
                  key={stage.num}
                  data-testid={`presence-stage-${i + 1}`}
                  className="relative grid grid-cols-1 items-center gap-8 pl-14 sm:pl-16 lg:grid-cols-2 lg:gap-20 lg:pl-0"
                >
                  <span aria-hidden="true" className="absolute left-[11px] top-2 h-4 w-4 rounded-full border border-gold bg-ink lg:left-1/2 lg:-translate-x-1/2" />

                  {image ? (
                    <div className={flip ? "lg:order-2" : ""}>
                      <Reveal>
                        <StageImage src={image} alt={stage.title} />
                      </Reveal>
                    </div>
                  ) : (
                    <div className={`hidden items-center justify-center lg:flex ${flip ? "lg:order-2" : ""}`}>
                      <Reveal>
                        <span className="font-display text-8xl text-transparent" style={{ WebkitTextStroke: "1px rgba(212,175,55,0.5)" }}>
                          {stage.num}
                        </span>
                      </Reveal>
                    </div>
                  )}

                  <div className={flip ? "lg:order-1 lg:text-right" : ""}>
                    <Reveal delay={0.1}>
                      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                        {stage.num} · {stage.place}
                      </p>
                    </Reveal>
                    <Reveal delay={0.18}>
                      <h3 className="mt-3 font-serif text-3xl font-light text-parchment sm:text-4xl">
                        {stage.title}
                      </h3>
                    </Reveal>
                    <Reveal delay={0.26}>
                      <p className={`mt-4 max-w-md text-sm font-light leading-relaxed text-sand sm:text-base ${flip ? "lg:ml-auto" : ""}`}>
                        {stage.text}
                      </p>
                    </Reveal>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 flex flex-wrap items-center gap-4 lg:mt-28">
            <a
              data-testid="presence-external-link"
              href={PRESENCE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-parchment/70 underline decoration-gold/40 underline-offset-8 transition-colors duration-300 hover:text-gold"
            >
              {t.presence.cta_more} <ArrowUpRight size={14} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Presence;
